import { isNil } from 'lodash';
import React, { useContext } from 'react';
import { MBView } from '@shared/components/MBView';
import { View } from '@shared/components/View';
import { MBBlockLabel } from '@shared/components/blocks/utils/label';
import { MBIconV2, MBIconV2Name } from '@shared/components/image';
import { MBTextBody2 } from '@shared/components/typography';
import { useResponsiveFormContext } from '@shared/scenes/entity/components/ResponsiveFormContext';
import { ColorThemeContext, grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { isInputComponent } from '../../utils';
import { WorkflowComponentStyles } from './WorkflowComponentStyles';

interface Props {
  children: React.ReactNode;
  extraChildren?: React.ReactNode;
  paddingHorizontal: number;
  iconPad: number;
  component: API.AnyComponent;
  showLabel: boolean;
  showIcon?: boolean;
  error?: string;
}

export function WorkflowComponentWrapper({
  children,
  extraChildren,
  paddingHorizontal,
  iconPad,
  component,
  showLabel,
  showIcon = true,
  error,
}: Props) {
  const responsiveForms = useResponsiveFormContext();
  const colors = useContext(ColorThemeContext);
  const hasError = !isNil(error);

  if (!isInputComponent(component)) {
    return null;
  }

  return (
    <MBView
      style={[
        WorkflowComponentStyles.wrapper,
        hasError && {
          borderColor: colors.error.border,
          backgroundColor: colors.error.background,
        },
        showLabel && {
          paddingVertical: grid(1.75),
        },
        responsiveForms && {
          paddingVertical: 0,
          marginVertical: 0,
        },
      ]}
    >
      <MBView
        paddingHorizontal={paddingHorizontal}
        fullWidth
        style={responsiveForms ? undefined : WorkflowComponentStyles.label}
      >
        <View row centerVertically style={{ flexGrow: 1 }}>
          {!isNil(component.icon) && showIcon && (
            <MBView paddingRight={iconPad} style={{ flexShrink: 0, flexGrow: 0 }}>
              <MBIconV2
                color={!isNil(error) ? colors.error.foreground : colors.accent}
                name={component.icon as MBIconV2Name}
                size={40}
              />
            </MBView>
          )}
          <View flex style={{ flexDirection: 'column', flexShrink: 0, flexGrow: 1 }}>
            {showLabel && (
              <View>
                <MBBlockLabel label={component.label} />
              </View>
            )}
            {children}
          </View>
        </View>
        {extraChildren && (
          <View paddingLeft={showIcon ? 40 / 8 + iconPad : 0}>{extraChildren}</View>
        )}
        {hasError && (
          <MBView style={WorkflowComponentStyles.error}>
            <MBTextBody2 color={colors.error.foreground}>{error}</MBTextBody2>
          </MBView>
        )}
      </MBView>
    </MBView>
  );
}
